<template>
  <div class="mensagens-programadas-lote-form">
    <div class="form-group">
      <label>{{ t('Período - Data Último Compromisso') }}</label>
      <date-range
        v-model="dateRangeValue"
        :placeholder="t('Selecione o período')"
        elem-id="mensagens-programadas-lote-date-range"
        data-testid="mensagens-programadas-lote-date-range"
        :predefined-buttons="dateRangePredefinedButtons"
      />
    </div>

    <div class="form-group">
      <label>{{ t('Quantidade de Clientes') }}</label>
      <input-number
        v-model="formData.quantidade"
        :placeholder="t('Ex: 100')"
        :min="1"
        :max="10000"
        elem-id="mensagens-programadas-lote-quantidade"
        data-testid="mensagens-programadas-lote-quantidade-input"
      />
      <small class="help-block">{{ t('Número máximo de clientes a serem selecionados') }}</small>
    </div>

    <div class="form-group">
      <label>{{ t('Template de Mensagem') }}</label>
      <div class="template-input-group">
        <input-text
          :model-value="selectedTemplateName"
          :placeholder="t('Nenhum template selecionado')"
          readonly
          fluid
          elem-id="mensagens-programadas-lote-template-name"
          data-testid="mensagens-programadas-lote-template-name-input"
        />
        <Button
          :label="t('Escolher Template')"
          icon="pi pi-search"
          severity="info"
          data-testid="mensagens-programadas-lote-choose-template-btn"
          @click="openTemplateSelection"
        />
      </div>
      <div v-if="formData.template" class="template-preview">
        <strong>{{ t('Prévia da mensagem:') }}</strong>
        <div v-html="sanitizeHtmlContent(formData.template.template_content)" class="template-content"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { sanitizeHtmlContent } from '@/utils/sanitizeHtmlContent';
import { eventBus } from '@/eventBus';
import { useFiltro } from '@/composables/useFiltro';
import { log } from '@/utils/DebugLogger';

interface MessageTemplate {
  template_id: number | string;
  template_name: string;
  template_content?: string;
}

interface FormDataShape {
  dataInicio: string;
  dataFim: string;
  quantidade: number;
  template: MessageTemplate | null;
}

interface SearchPayload {
  dataInicio: string;
  dataFim: string;
  quantidade: number;
  template: MessageTemplate | null;
}

interface PredefinedButton {
  period: string;
  label: string;
}

const { getSelectedFranchises } = useFiltro();

const { t } = useI18n();

// DateRange v-model: ISO `YYYY-MM-DD - YYYY-MM-DD` (locale-independent).
// The display field inside DateRange still renders locale-formatted dates.
const dateRangeValue = ref<string>('');
const dateRangePredefinedButtons = ref<PredefinedButton[]>([
  { period: 'lastmonth', label: t('Mês passado') },
  { period: 'lastyear', label: t('Último ano') },
  { period: '1.5years', label: t('1 ano e meio') },
  { period: '2years', label: t('2 anos') },
]);

// Props
interface Props {
  isSearching?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isSearching: false,
});

// Emits
const emit = defineEmits<{
  search: [payload: SearchPayload];
}>();

// Data
const formData = ref<FormDataShape>({
  dataInicio: '',
  dataFim: '',
  quantidade: 100,
  template: null,
});

// Computed
const selectedTemplateName = computed<string>(() => {
  return formData.value.template ? formData.value.template.template_name : '';
});

const isFormValid = computed<boolean>(() => {
  return !!formData.value.dataInicio &&
         !!formData.value.dataFim &&
         formData.value.quantidade > 0 &&
         formData.value.template !== null;
});

// Watch the date range string and split into dataInicio / dataFim (dd/mm/yyyy)
watch(dateRangeValue, (newValue) => {
  if (!newValue) {
    formData.value.dataInicio = '';
    formData.value.dataFim = '';
    return;
  }
  const parts = newValue.split(' - ');
  if (parts.length === 2) {
    formData.value.dataInicio = parts[0].trim();
    formData.value.dataFim = parts[1].trim();
    log('MensagensProgramadasLoteForm', 'dateRangeWatch', 'Parsed date range', {
      dataInicio: formData.value.dataInicio,
      dataFim: formData.value.dataFim,
    });
  }
});

// Template Methods
const openTemplateSelection = (): void => {
  log('MensagensProgramadasLoteForm', 'openTemplateSelection', 'Opening template selection');

  // Get franchise IDs from filter
  const franchisesObj = getSelectedFranchises() as Record<string, unknown>;
  const franchiseIds = Object.keys(franchisesObj).map(id => parseInt(id, 10));

  log('MensagensProgramadasLoteForm', 'openTemplateSelection', 'Franchise IDs', franchiseIds);

  eventBus.emit('template.open-selection', {
    franchiseIds,
    callback: handleTemplateSelected,
  });
};

const handleTemplateSelected = (template: MessageTemplate): void => {
  log('MensagensProgramadasLoteForm', 'handleTemplateSelected', 'Template selected', template);
  formData.value.template = template;
};

const handleSearch = (): void => {
  if (!isFormValid.value) {
    eventBus.emit('globalMessage.error', t('Por favor, preencha todos os campos obrigatórios.'));
    return;
  }

  log('MensagensProgramadasLoteForm', 'handleSearch', 'Searching customers', formData.value);

  emit('search', {
    dataInicio: formData.value.dataInicio,
    dataFim: formData.value.dataFim,
    quantidade: formData.value.quantidade,
    template: formData.value.template,
  });
};

// Expose form data and methods for parent access
defineExpose({
  formData,
  isFormValid,
  handleSearch,
});
</script>

<style scoped>
.mensagens-programadas-lote-form {
  padding: 15px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.help-block {
  display: block;
  margin-top: 5px;
  font-size: 0.875rem;
  color: #666;
}

.template-input-group {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

.template-input-group :deep(.p-inputtext) {
  flex: 1;
}

.template-preview {
  margin-top: 10px;
  padding: 15px;
  background-color: #f4f4f4;
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.template-preview strong {
  display: block;
  margin-bottom: 8px;
  color: #333;
}

.template-content {
  margin-top: 5px;
  line-height: 1.5;
}
</style>
